import type { I18nService } from '@recruitee/i18n';

import type { CandidateDataTableColumn } from './candidates-table.types';
import { CandidateColumnId } from './candidates-table.types';

// eslint-disable-next-line max-lines-per-function
export const buildCandidatesTableColumns = (
  i18n: I18nService,
): Record<CandidateColumnId, CandidateDataTableColumn> => ({
  [CandidateColumnId.Name]: {
    id: CandidateColumnId.Name,
    label: i18n.translate('app.search.table.columns.name'),
    geometry: { width: 280, minWidth: 200 },
    options: { draggable: false, removable: false, resizable: true, sortable: true },
  },
  [CandidateColumnId.Note]: {
    id: CandidateColumnId.Note,
    label: i18n.translate('app.search.table.columns.note'),
    geometry: { width: 380, minWidth: 300 },
    options: { draggable: false, removable: false, resizable: true, sortable: false },
  },
  [CandidateColumnId.PositiveRating]: {
    id: CandidateColumnId.PositiveRating,
    label: i18n.translate('app.search.table.columns.average_evaluation_score'),
    geometry: { width: 100, minWidth: 100, maxWidth: 120, autoFitProp: 'maxWidth' },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.Stages]: {
    id: CandidateColumnId.Stages,
    label: i18n.translate('app.search.table.columns.stage'),
    geometry: { width: 200, minWidth: 100 },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.JobFitScore]: {
    id: CandidateColumnId.JobFitScore,
    label: i18n.translate('app.search.table.columns.evaluation_score'),
    geometry: { width: 100, minWidth: 100, maxWidth: 120, autoFitProp: 'maxWidth' },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.Jobs]: {
    id: CandidateColumnId.Jobs,
    label: i18n.translate('app.search.table.columns.job'),
    geometry: { width: 250, minWidth: 100 },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.TalentPools]: {
    id: CandidateColumnId.TalentPools,
    label: i18n.translate('app.search.table.columns.talent_pool'),
    geometry: { width: 250, minWidth: 100 },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.EEOStatus]: {
    id: CandidateColumnId.EEOStatus,
    label: i18n.translate('app.search.table.columns.eeo_status'),
    geometry: { width: 160, minWidth: 100 },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.Tags]: {
    id: CandidateColumnId.Tags,
    label: i18n.translate('app.search.table.columns.tags'),
    geometry: { width: 250, minWidth: 100 },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.Sources]: {
    id: CandidateColumnId.Sources,
    label: i18n.translate('app.search.table.columns.sources'),
    geometry: { width: 250, minWidth: 100 },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.CreatedAt]: {
    id: CandidateColumnId.CreatedAt,
    label: i18n.translate('app.search.table.columns.profile_created'),
    geometry: { width: getCreatedAtColumnWidth(i18n.locale) },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.NoteCreatedAt]: {
    id: CandidateColumnId.NoteCreatedAt,
    label: i18n.translate('app.search.table.columns.note_created'),
    geometry: { width: getCreatedAtColumnWidth(i18n.locale) },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.GdprStatus]: {
    id: CandidateColumnId.GdprStatus,
    label: i18n.translate('app.search.table.columns.gdpr_status'),
    geometry: { width: 120, minWidth: 100, maxWidth: 150 },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.ExpiringIn]: {
    id: CandidateColumnId.ExpiringIn,
    label: i18n.translate('app.search.table.columns.expiring_in'),
    geometry: { width: 140 },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.RequestedChange]: {
    id: CandidateColumnId.RequestedChange,
    label: i18n.translate('app.search.table.columns.requested_change'),
    geometry: { width: 200 },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.RequestedDelete]: {
    id: CandidateColumnId.RequestedDelete,
    label: i18n.translate('app.search.table.columns.requested_delete'),
    geometry: { width: 200 },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.DisqualifiedBy]: {
    id: CandidateColumnId.DisqualifiedBy,
    label: i18n.translate('app.search.table.columns.disqualified_by'),
    geometry: { width: 150, minWidth: 120 },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.DisqualifyDate]: {
    id: CandidateColumnId.DisqualifyDate,
    label: i18n.translate('app.search.table.columns.disqualify_date'),
    geometry: { width: 140 },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.LastActivity]: {
    id: CandidateColumnId.LastActivity,
    label: i18n.translate('app.search.table.columns.last_activity'),
    geometry: { width: 120 },
    options: { draggable: false, removable: true, resizable: false, sortable: true },
  },
  [CandidateColumnId.DeletedAt]: {
    id: CandidateColumnId.DeletedAt,
    label: i18n.translate('app.search.table.columns.deleted_at'),
    geometry: { width: 120 },
    options: { draggable: false, removable: false, resizable: false, sortable: true },
  },
  [CandidateColumnId.DeletedBy]: {
    id: CandidateColumnId.DeletedBy,
    label: i18n.translate('app.search.table.columns.deleted_by'),
    geometry: { width: 200 },
    options: { draggable: false, removable: false, resizable: false, sortable: true },
  },
  [CandidateColumnId.Integrations]: {
    id: CandidateColumnId.Integrations,
    label: i18n.translate('app.search.table.columns.integrations'),
    geometry: { width: 140 },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.HiredAt]: {
    id: CandidateColumnId.HiredAt,
    label: i18n.translate('app.search.table.columns.hired_at'),
    geometry: { width: 100, minWidth: 100, maxWidth: 120, autoFitProp: 'maxWidth' },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.JobStartsAt]: {
    id: CandidateColumnId.JobStartsAt,
    label: i18n.translate('app.search.table.columns.job_starts_at'),
    geometry: { width: 100, minWidth: 100, maxWidth: 120, autoFitProp: 'maxWidth' },
    options: { draggable: false, removable: true, resizable: true, sortable: true },
  },
  [CandidateColumnId.WorkLocation]: {
    id: CandidateColumnId.WorkLocation,
    label: i18n.translate('app.search.table.columns.work_location'),
    geometry: { width: 200, minWidth: 100, autoFitProp: 'width' },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.JobLocationName]: {
    id: CandidateColumnId.JobLocationName,
    label: i18n.translate('app.search.table.columns.job_location_name'),
    geometry: { width: 200, minWidth: 100, autoFitProp: 'width' },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
  [CandidateColumnId.JobLocationAddress]: {
    id: CandidateColumnId.JobLocationAddress,
    label: i18n.translate('app.search.table.columns.job_location_address'),
    geometry: { width: 250, minWidth: 100, autoFitProp: 'width' },
    options: { draggable: false, removable: true, resizable: true, sortable: false },
  },
});

export const getCreatedAtColumnWidth = (locale: string) => {
  switch (locale) {
    case 'fr':
      return 110;

    default:
      return 140;
  }
};
